import { Copy, Edit, Key, Plus, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { Checkbox } from '../../components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Textarea } from '../../components/ui/textarea'
import { useApiKeys } from '../../hooks/useApiKeys'

import { showToast } from '@/utils/toastMessage'

const ExternalApiSettings = () => {
  const {
    isLoading,
    keys: apiKeys,
    generatedKey,
    load: getApiKeys,
    generate: generateApiKey,
    update: updateApiKey,
    remove: deleteApiKey,
  } = useApiKeys()

  const [newKeyDescription, setNewKeyDescription] = useState('')
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([
    'write:customers',
    'write:bookings',
  ])
  const [editingKeyId, setEditingKeyId] = useState<string | null>(null)
  const [editingPermissions, setEditingPermissions] = useState<string[]>([])

  const permissionGroups = {
    write: [
      { value: 'write:bookings', label: 'Reservierungen verwalten' },
      { value: 'write:customers', label: 'Kunden verwalten' },
    ],
  }

  useEffect(() => {
    getApiKeys()
  }, [getApiKeys])

  const handlePermissionChange = (permission: string, checked: boolean | 'indeterminate') => {
    const isChecked = checked === true
    const currentPermissions = editingKeyId ? editingPermissions : selectedPermissions
    const setPermissions = editingKeyId ? setEditingPermissions : setSelectedPermissions

    if (isChecked) {
      setPermissions([...currentPermissions, permission])
    } else {
      setPermissions(currentPermissions.filter((p) => p !== permission))
    }
  }

  const handleGenerateApiKey = async () => {
    if (!newKeyDescription.trim()) {
      showToast({ severity: 'error', detail: 'Bitte geben Sie eine Beschreibung ein.' })
      return
    }

    await generateApiKey(newKeyDescription.trim(), selectedPermissions)
    setNewKeyDescription('')
    setSelectedPermissions(['write:customers', 'write:bookings'])
  }

  const handleEditPermissions = (keyId: string, permissions: string[]) => {
    setEditingKeyId(keyId)
    setEditingPermissions(permissions)
  }

  const handleSavePermissions = async () => {
    if (editingKeyId) {
      await updateApiKey(editingKeyId, editingPermissions)
      setEditingKeyId(null)
      setEditingPermissions([])
    }
  }

  const handleCancelEdit = () => {
    setEditingKeyId(null)
    setEditingPermissions([])
  }

  const handleDeleteApiKey = async (keyId: string) => {
    await deleteApiKey(keyId)
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      showToast({ severity: 'success', detail: 'API-Schlüssel in Zwischenablage kopiert' })
    } catch {
      showToast({ severity: 'error', detail: 'Fehler beim Kopieren' })
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Externe API</h2>
        <p className="text-muted-foreground text-sm">
          Verwalten Sie API-Schlüssel für den externen Zugriff auf Ihre Daten.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Neuen API-Schlüssel erstellen
          </CardTitle>
          <CardDescription>
            Erstellen Sie einen neuen API-Schlüssel für den externen Zugriff auf Ihre Buchungsdaten.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="description">Beschreibung</Label>
            <Textarea
              id="description"
              placeholder="z.B. Integration für Buchungssystem"
              value={newKeyDescription}
              onChange={(e) => setNewKeyDescription(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Berechtigungen</Label>
            <div className="space-y-4">
              {Object.entries(permissionGroups).map(([groupName, permissions]) => (
                <div key={groupName} className="space-y-2">
                  <h4 className="text-sm font-medium capitalize">
                    {groupName === 'read'
                      ? 'Lesen'
                      : groupName === 'write'
                        ? 'Verwalten'
                        : 'Löschen'}
                  </h4>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    {permissions.map((option) => (
                      <div key={option.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={option.value}
                          checked={selectedPermissions.includes(option.value)}
                          onCheckedChange={(checked) =>
                            handlePermissionChange(option.value, checked)
                          }
                        />
                        <Label
                          htmlFor={option.value}
                          className="cursor-pointer text-sm font-normal"
                        >
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <Button
            onClick={handleGenerateApiKey}
            disabled={isLoading || !newKeyDescription.trim()}
            className="w-full"
          >
            <Plus className="mr-2 h-4 w-4" />
            {isLoading ? 'Wird erstellt...' : 'API-Schlüssel erstellen'}
          </Button>
        </CardContent>
      </Card>
      {generatedKey && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="text-green-800">API-Schlüssel generiert</CardTitle>
            <CardDescription className="text-green-700">
              Kopieren Sie diesen Schlüssel jetzt. Er wird nur einmal angezeigt und kann später
              nicht mehr abgerufen werden.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Input value={generatedKey} readOnly className="font-mono" />
              <Button onClick={() => copyToClipboard(generatedKey)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>API-Schlüssel</CardTitle>
          <CardDescription>
            Ihre aktiven API-Schlüssel. Verwenden Sie diese für den externen API-Zugriff.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {apiKeys.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center">
              Noch keine API-Schlüssel vorhanden.
            </p>
          ) : (
            <div className="space-y-4">
              {apiKeys.map((key) => (
                <div
                  key={key.id}
                  className="flex items-center justify-between rounded-lg border p-4"
                >
                  <div className="space-y-1">
                    <p className="font-medium">{key.description}</p>
                    <p className="text-muted-foreground text-sm">
                      Erstellt am {new Date(key.issuedAt).toLocaleDateString('de-DE')}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                        Aktiv
                      </span>
                      {key.expiresAt && (
                        <span className="text-muted-foreground text-xs">
                          Läuft ab: {new Date(key.expiresAt).toLocaleDateString('de-DE')}
                        </span>
                      )}
                    </div>
                    {key.permissions && key.permissions.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {key.permissions.map((permission) => {
                          const allPermissions = permissionGroups.write
                          const label =
                            allPermissions.find((opt) => opt.value === permission)?.label ||
                            permission
                          return (
                            <Badge key={permission} variant="secondary" className="text-xs">
                              {label}
                            </Badge>
                          )
                        })}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(key.id)}
                      className="flex items-center gap-2"
                    >
                      <Copy className="h-4 w-4" />
                      ID kopieren
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditPermissions(key.id, key.permissions || [])}
                      className="flex items-center gap-2"
                    >
                      <Edit className="h-4 w-4" />
                      Berechtigungen bearbeiten
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteApiKey(key.id)}
                      className="flex items-center gap-2"
                    >
                      <Trash2 className="h-4 w-4" />
                      Widerrufen
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editingKeyId} onOpenChange={() => handleCancelEdit()}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Berechtigungen bearbeiten</DialogTitle>
            <DialogDescription>
              Ändern Sie die Berechtigungen für diesen API-Schlüssel.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-4">
              {Object.entries(permissionGroups).map(([groupName, permissions]) => (
                <div key={groupName} className="space-y-2">
                  <h4 className="text-sm font-medium capitalize">
                    {groupName === 'read'
                      ? 'Lesen'
                      : groupName === 'write'
                        ? 'Verwalten'
                        : 'Löschen'}
                  </h4>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    {permissions.map((option) => (
                      <div key={option.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={`edit-${option.value}`}
                          checked={editingPermissions.includes(option.value)}
                          onCheckedChange={(checked) =>
                            handlePermissionChange(option.value, checked)
                          }
                        />
                        <Label
                          htmlFor={`edit-${option.value}`}
                          className="cursor-pointer text-sm font-normal"
                        >
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelEdit}>
              Abbrechen
            </Button>
            <Button onClick={handleSavePermissions} disabled={isLoading}>
              {isLoading ? 'Wird gespeichert...' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ExternalApiSettings
